import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  ChevronRight,
  Play,
  Calendar,
  Clock,
  User,
  Bookmark,
  CheckCircle2,
  PlayCircle,
  Circle,
  FileText,
  Presentation,
  FileArchive,
  FileType2,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Film,
  Download,
  Eye,
  ShieldAlert,
  RefreshCw,
  Code2,
  HelpCircle,
  Lock,
  Award,
  MessageCircle,
} from "lucide-react";
import apiClient from "../../../utils/apiClient";
import useMaterialViewer, { isViewableType } from "../../Materials/useMaterialViewer";
import { canDownloadFile } from "../../ContentProtection/contentAccess";
import { getCurrentUser } from "../../../utils/auth";
import { isVideoSaved, toggleSavedVideo } from "../../../utils/savedVideos";
import { usePlan } from "../../../contexts/PlanContext";
import { fetchSessionByVideo, fetchGamification } from "../../../utils/smartLearning";
import SessionDiscussionTab from "../LearningPath/SessionDiscussionTab";
import { isDesktopApp } from "../../../utils/desktopApp";
import DesktopAppRequired from "../../Common/DesktopAppRequired";
import ProtectedVideoPlayer from "../../Common/ProtectedVideoPlayer";
import useVideoWatchTracker from "../../../hooks/useVideoWatchTracker";
import { runPlaybackSafetyCheck, isDevtoolsOpen, GUARD_CONFIG } from "../../../utils/playbackGuard";
import "./StudentVideoPlayer.scss";

/**
 * StudentVideoPlayer (fresh / non-session-based variant)
 *
 * Wired student video-player page. Reuses the platform's video-security stack
 * (presigned short-lived stream URLs + Common/ProtectedVideoPlayer — the
 * watermark, useVideoProtection and our own controls — + desktop-app gating)
 * and feeds real watch progress via useVideoWatchTracker. Video download is
 * intentionally removed/blocked.
 *
 * Route: /s/watch-v2/:id (temporary — see App.js)
 *
 * Data sources:
 *   • GET /course-assignments/my-enrollments        — course switcher
 *   • GET /videos/student/my-videos?batchId=         — current video + neighbours
 *   • GET /videos/:id/stream                          — presigned playback URL
 *   • GET /videos/batch/:id/view-status               — progress / baseline
 *   • GET /materials/video/:id  +  /materials/batch/:id/by-video/:id
 *   • GET /materials/:id/download                      — material link
 *   • GET /student/me/learning-overview?batchId=       — progress + continue
 */

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "";

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return "";
  const totalSec = Math.round(minutes * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const iconForType = (fileType = "") => {
  const t = String(fileType).toLowerCase();
  if (t.includes("pdf")) return { Icon: FileText, color: "#7c5cff" };
  if (t.includes("ppt") || t.includes("presentation")) return { Icon: Presentation, color: "#f59e0b" };
  if (t.includes("zip") || t.includes("rar") || t.includes("archive")) return { Icon: FileArchive, color: "#3b82f6" };
  if (t.includes("doc")) return { Icon: FileText, color: "#2563eb" };
  return { Icon: FileType2, color: "#64748b" };
};

const STATUS_META = {
  completed: { label: "Completed", Icon: CheckCircle2, cls: "is-completed" },
  "in-progress": { label: "In Progress", Icon: PlayCircle, cls: "is-progress" },
  "not-started": { label: "Not Started", Icon: Circle, cls: "is-pending" },
};

const deriveStatus = (vs) => {
  const percent = vs?.percent ?? (vs?.viewed ? 100 : 0);
  const status = percent >= 100 ? "completed" : percent > 0 ? "in-progress" : "not-started";
  return { percent, status };
};

const PROGRESS_LABELS = { classes: "Videos", assignments: "Assignments", challenges: "Challenges", quizzes: "Quizzes" };
const PROGRESS_COLORS = { classes: "#7c5cff", assignments: "#f59e0b", challenges: "#10b981", quizzes: "#3b82f6" };

// ── Secure video (ProtectedVideoPlayer + watch tracking) ──
// The player brings the watermark, the protection hook and its own controls;
// this adds what only this page knows — the resume point and watch time.
const SecureVideo = ({ url, poster, loading, title, videoId, durationMin, baselineSec, resumeSec, onProgress, onExpired }) => {
  const videoRef = useRef(null);

  useVideoWatchTracker({
    videoRef,
    videoId,
    durationMin,
    baselineSec,
    active: !!url,
    onProgress,
  });

  return (
    <div className="svp-player__stage">
      {loading ? (
        <div className="svp-player__center"><Spinner animation="border" variant="light" /></div>
      ) : url ? (
        // `resumeSec` is the stored PLAYHEAD, not the cumulative watch time —
        // seeking to the latter overshoots for anyone who rewound. It can
        // arrive after the stream URL; the player applies it once per video.
        <ProtectedVideoPlayer
          ref={videoRef}
          src={url}
          poster={poster}
          title={title}
          preload="auto"
          startAt={resumeSec}
          mediaKey={videoId}
          durationHint={durationMin > 0 ? durationMin * 60 : 0}
          onExpired={onExpired}
        />
      ) : (
        <div className="svp-player__center svp-player__unavailable">
          <Film size={32} />
          <p>Video unavailable</p>
        </div>
      )}
    </div>
  );
};

// ── Playback blocked panel (shown instead of the player when unsafe) ──
const REASON_TEXT = {
  "ad-blocker": "An ad / content blocker is active. Please disable it for this page, then re-check.",
  devtools: "Developer tools appear to be open. Please close them, then re-check.",
};

const PlaybackBlocked = ({ reasons, checking, onRecheck }) => (
  <div className="svp-player__stage">
    <div className="svp-guard">
      <ShieldAlert size={34} className="svp-guard__icon" />
      <h3 className="svp-guard__title">Protected video</h3>
      <ul className="svp-guard__reasons">
        {reasons.map((r) => (
          <li key={r}>{REASON_TEXT[r] || "Your browser environment isn’t allowed for secure playback."}</li>
        ))}
      </ul>
      <button type="button" className="svp-guard__btn" onClick={onRecheck} disabled={checking}>
        <RefreshCw size={15} className={checking ? "is-spin" : ""} />
        {checking ? "Checking…" : "Re-check & play"}
      </button>
    </div>
  </div>
);

// ── Progress donut ───────────────────────────────────────────────────────────
const ProgressDonut = ({ overall, segments }) => {
  const R = 46;
  const C = 2 * Math.PI * R;
  const total = segments.reduce((a, s) => a + s.done, 0);
  let cumulative = 0;
  return (
    <div className="svp-donut">
      <svg viewBox="0 0 120 120" className="svp-donut__svg">
        <circle cx="60" cy="60" r={R} className="svp-donut__track" strokeWidth="12" fill="none" />
        {total > 0 &&
          segments.map((seg) => {
            if (seg.done <= 0) return null;
            const len = (seg.done / total) * C;
            const dashOffset = -cumulative;
            cumulative += len;
            return (
              <circle
                key={seg.key}
                cx="60" cy="60" r={R} fill="none" strokeWidth="12" strokeLinecap="butt"
                stroke={seg.color}
                strokeDasharray={`${len} ${C - len}`}
                strokeDashoffset={dashOffset}
                transform="rotate(-90 60 60)"
              />
            );
          })}
      </svg>
      <div className="svp-donut__center">
        <span className="svp-donut__pct">{overall}%</span>
        <span className="svp-donut__label">Overall</span>
      </div>
    </div>
  );
};

// ── Material row ──
// Viewable docs (pdf/docx/ppt/xls/img/text) open in the in-app viewer; only true
// non-viewable types fall back to a download.
const MaterialRow = ({ mat, onView, onDownload }) => {
  const { Icon, color } = iconForType(mat.fileType);
  const size = formatBytes(mat.fileSize);
  // A view-only file always opens in the viewer, never as a download.
  const viewable = isViewableType(mat.fileType) || !canDownloadFile(mat);
  return (
    <li className="svp-resrow">
      <span className="svp-resrow__icon" style={{ background: `${color}1a`, color }}>
        <Icon size={18} />
      </span>
      <div className="svp-resrow__info">
        <span className="svp-resrow__name">{mat.title}</span>
        {(size || mat.fileType) && (
          <span className="svp-resrow__size">{size || String(mat.fileType).toUpperCase()}</span>
        )}
      </div>
      <button
        type="button"
        className="svp-iconbtn"
        aria-label={`${viewable ? "View" : "Download"} ${mat.title}`}
        onClick={() => (viewable ? onView(mat) : onDownload(mat))}
      >
        {viewable ? <Eye size={16} /> : <Download size={16} />}
      </button>
    </li>
  );
};

// ── Session-progress ring (Overview, smart-learning only) ──
const MiniRing = ({ value = 0, caption }) => {
  const R = 34;
  const C = 2 * Math.PI * R;
  const off = C * (1 - Math.min(100, Math.max(0, value)) / 100);
  return (
    <div className="svp-ring">
      <svg viewBox="0 0 90 90" className="svp-ring__svg">
        <circle cx="45" cy="45" r={R} className="svp-ring__track" strokeWidth="8" fill="none" />
        <circle
          cx="45" cy="45" r={R} className="svp-ring__bar" strokeWidth="8" fill="none"
          strokeLinecap="round" strokeDasharray={C} strokeDashoffset={off}
          transform="rotate(-90 45 45)"
        />
      </svg>
      <span className="svp-ring__pct">{value}%</span>
      {caption && <span className="svp-ring__caption">{caption}</span>}
    </div>
  );
};

// ── "What's Unlocked" resource card (assignment / coding / quiz) ──
const UNLOCK_META = {
  assignment: { Icon: FileText, label: "Assignment", color: "#3b82f6" },
  codingChallenge: { Icon: Code2, label: "Coding Challenge", color: "#10b981" },
  quiz: { Icon: HelpCircle, label: "Quiz", color: "#f59e0b" },
  document: { Icon: FileText, label: "Material", color: "#7c5cff" },
};

const UnlockedCard = ({ res, unlocked, done }) => {
  const meta = UNLOCK_META[res.kind] || UNLOCK_META.document;
  const { Icon } = meta;
  const inner = (
    <>
      <span className="svp-unlocked__icon" style={{ background: `${meta.color}1a`, color: meta.color }}>
        <Icon size={18} />
      </span>
      <div className="svp-unlocked__body">
        <span className="svp-unlocked__kind">{meta.label}</span>
        <span className="svp-unlocked__title">{res.title || meta.label}</span>
      </div>
      <span className={`svp-unlocked__badge ${done ? "is-done" : unlocked ? "is-unlocked" : "is-locked"}`}>
        {done ? (
          <><CheckCircle2 size={12} /> Completed</>
        ) : unlocked ? (
          <><CheckCircle2 size={12} /> Unlocked</>
        ) : (
          <><Lock size={11} /> Locked</>
        )}
      </span>
    </>
  );
  if (unlocked && res.href) {
    return (
      <a className="svp-unlocked__card" href={res.href} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }
  return <div className={`svp-unlocked__card${unlocked ? "" : " is-disabled"}`}>{inner}</div>;
};

// ── Session document row ("Materials for this session" / Resources) ──
// Opens in the in-app viewer, like every other material — so the document's
// content protection (view only / no copy) applies here too.
const SessionDocRow = ({ doc, unlocked, onOpen }) => {
  const { Icon, color } = iconForType(doc.fileType);
  const size = formatBytes(doc.fileSize);
  if (unlocked && doc.refId) {
    return (
      <li className="svp-resrow">
        <span className="svp-resrow__icon" style={{ background: `${color}1a`, color }}>
          <Icon size={18} />
        </span>
        <div className="svp-resrow__info">
          <span className="svp-resrow__name">{doc.title}</span>
          {(size || doc.fileType) && (
            <span className="svp-resrow__size">{size || String(doc.fileType).toUpperCase()}</span>
          )}
        </div>
        <button
          type="button"
          className="svp-iconbtn"
          aria-label={`Open ${doc.title}`}
          onClick={() => onOpen({ _id: doc.refId, title: doc.title, fileType: doc.fileType })}
        >
          <Eye size={16} />
        </button>
      </li>
    );
  }
  return (
    <li className="svp-resrow is-locked">
      <span className="svp-resrow__icon" style={{ background: `${color}1a`, color }}>
        <Icon size={18} />
      </span>
      <div className="svp-resrow__info">
        <span className="svp-resrow__name">{doc.title}</span>
        <span className="svp-resrow__size">Unlocks after class</span>
      </div>
      <Lock size={15} />
    </li>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const StudentVideoPlayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPlanFeature } = usePlan();
  const currentUser = getCurrentUser();
  const { openMaterial, downloadMaterial, viewer: materialViewer } = useMaterialViewer();

  // Context handed over by the v2 learning page (optional — direct deep-links
  // won't have it, and the loader falls back to discovering the batch).
  const navState = location.state || {};
  const stateBatchId = navState.batchId || null;

  const [loading, setLoading] = useState(true);
  const [rec, setRec] = useState(null);
  const [recordings, setRecordings] = useState([]); // same-batch list (for prev/next)
  const [courseThumb, setCourseThumb] = useState("");
  const [videoSecurity, setVideoSecurity] = useState(false);
  // Seed from nav state so the header shows instantly; refined after fetch.
  const [course, setCourse] = useState(
    navState.courseName ? { name: navState.courseName, code: navState.courseCode || "" } : null
  );

  const [videoUrl, setVideoUrl] = useState("");
  const [streamLoading, setStreamLoading] = useState(true);

  // Playback safety guard: 'checking' | 'safe' | 'blocked'. The stream URL is
  // only requested while 'safe'. NOTE: heuristic / bypassable — deterrent only.
  const [guard, setGuard] = useState("checking");
  const [guardReasons, setGuardReasons] = useState([]);

  const [viewStatus, setViewStatus] = useState({});
  const [materials, setMaterials] = useState([]);
  const [overview, setOverview] = useState(null);

  const [tab, setTab] = useState("overview");

  // ── Smart Learning Flow: this recording's session (if any) ──
  // Resolved best-effort from the video id; null for legacy/non-session videos
  // so the player renders its plain layout unchanged.
  const [smartSession, setSmartSession] = useState(null);
  const [smartProgress, setSmartProgress] = useState(null);
  const [achievements, setAchievements] = useState([]);

  // Saved = the student's "watch later / liked" list (persisted locally).
  const studentId = currentUser?.studentId;
  const [saved, setSaved] = useState(() => isVideoSaved(studentId, id));
  useEffect(() => { setSaved(isVideoSaved(studentId, id)); }, [studentId, id]);
  const toggleSaved = useCallback(() => {
    const next = toggleSavedVideo(studentId, id);
    setSaved(next.includes(String(id)));
  }, [studentId, id]);

  const batchId = rec?.batch?._id || rec?.batch || null;

  const requireDesktopApp =
    hasPlanFeature("videoSecurityEnabled") && videoSecurity && !isDesktopApp() && currentUser?.usertype === "student";

  // ── Locate the video (+ neighbours, course, security) ──
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setRec(null);
    (async () => {
      // Enrollments (course switcher + fallback batch lookup)
      let enrollments = [];
      try {
        const enr = await apiClient.get("/course-assignments/my-enrollments");
        enrollments = enr?.data?.data || [];
      } catch { /* ignore */ }

      const pick = (resp) => ({
        list: resp?.data?.data || [],
        thumb: resp?.data?.courseThumbnailUrl || "",
        security: !!resp?.data?.videoSecurity,
      });

      let found = null;
      let list = [];
      let thumb = "";
      let security = false;

      // Fast path: the v2 page told us which batch this video is in.
      if (stateBatchId) {
        try {
          const r = pick(await apiClient.get(`/videos/student/my-videos?batchId=${stateBatchId}`));
          const hit = r.list.find((v) => v._id === id);
          if (hit) { found = hit; list = r.list; thumb = r.thumb; security = r.security; }
        } catch { /* ignore */ }
      }

      // …otherwise try the student's primary batch.
      if (!found) {
        try {
          const primary = pick(await apiClient.get("/videos/student/my-videos"));
          const hit = primary.list.find((v) => v._id === id);
          if (hit) { found = hit; list = primary.list; thumb = primary.thumb; security = primary.security; }
        } catch { /* ignore */ }
      }

      // …otherwise search the other enrolled batches.
      if (!found && enrollments.length) {
        const others = await Promise.all(
          enrollments.map((e) =>
            apiClient.get(`/videos/student/my-videos?batchId=${e.batchId?._id || e.batchId}`).then(pick).catch(() => null)
          )
        );
        for (const r of others) {
          if (!r) continue;
          const hit = r.list.find((v) => v._id === id);
          if (hit) { found = hit; list = r.list; thumb = r.thumb; security = r.security; break; }
        }
      }

      if (!alive) return;
      setRec(found);
      setRecordings(list);
      setCourseThumb(thumb);
      setVideoSecurity(security);

      // Resolve the course label from the matching enrolment
      const bId = found?.batch?._id || found?.batch;
      const match = enrollments.find((e) => String(e.batchId?._id || e.batchId) === String(bId));
      if (match) {
        // Refine the (possibly nav-seeded) course label from the enrolment.
        setCourse({
          name: match.courseId?.name || "Course",
          code: match.courseId?.code || match.batchId?.name || "",
        });
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [id, stateBatchId]);

  // ── Playback safety guard ──
  const runGuard = useCallback(async () => {
    setGuard("checking");
    const { safe, reasons } = await runPlaybackSafetyCheck();
    setGuardReasons(reasons);
    setGuard(safe ? "safe" : "blocked");
  }, []);

  // Run once on mount, and re-check when the window regains focus while blocked.
  useEffect(() => { runGuard(); }, [runGuard]);
  useEffect(() => {
    if (guard !== "blocked") return undefined;
    const onFocus = () => runGuard();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [guard, runGuard]);

  // While playing, keep watching THIS tab for devtools; re-gate if it opens.
  useEffect(() => {
    if (guard !== "safe" || !GUARD_CONFIG.blockOnDevtools) return undefined;
    const iv = window.setInterval(() => {
      if (isDevtoolsOpen()) {
        setGuardReasons(["devtools"]);
        setGuard("blocked");
      }
    }, 1500);
    return () => window.clearInterval(iv);
  }, [guard]);

  // ── Presigned stream URL (only fetched while the guard says it's safe) ──
  const fetchStreamUrl = useCallback(async () => {
    if (!id) return;
    setStreamLoading(true);
    try {
      const { data } = await apiClient.get(`/videos/${id}/stream`);
      setVideoUrl(data?.data?.url || "");
    } catch {
      setVideoUrl("");
    }
    setStreamLoading(false);
  }, [id]);

  // A fresh link for a stream that expired mid-watch. Unlike fetchStreamUrl it
  // never shows the loading state, so the player stays mounted and puts the
  // playhead back where it was on the new link.
  const refreshStreamUrl = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await apiClient.get(`/videos/${id}/stream`);
      if (data?.data?.url) setVideoUrl(data.data.url);
    } catch { /* the player offers "Try again" */ }
  }, [id]);

  useEffect(() => {
    if (guard === "safe") {
      fetchStreamUrl();
    } else {
      // Drop any handed-out link the moment we're no longer safe.
      setVideoUrl("");
    }
  }, [fetchStreamUrl, guard]);

  // ── View status (progress + baseline) ──
  useEffect(() => {
    if (!batchId) return;
    let alive = true;
    apiClient
      .get(`/videos/batch/${batchId}/view-status`)
      .then((res) => { if (alive) setViewStatus(res.data?.data || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [batchId]);

  // ── Materials for this video ──
  useEffect(() => {
    if (!id) return;
    let alive = true;
    (async () => {
      try {
        const videoMatsP = apiClient.get(`/materials/video/${id}`).catch(() => ({ data: { data: [] } }));
        const linkedMatsP = batchId
          ? apiClient.get(`/materials/batch/${batchId}/by-video/${id}`).catch(() => ({ data: { data: [] } }))
          : Promise.resolve({ data: { data: [] } });
        const [videoRes, linkedRes] = await Promise.all([videoMatsP, linkedMatsP]);
        if (!alive) return;
        const videoMats = (videoRes.data?.data || []).filter((m) => m.status === "ready");
        const linkedMats = linkedRes.data?.data || [];
        const seen = new Set(videoMats.map((m) => String(m._id)));
        setMaterials([...videoMats, ...linkedMats.filter((m) => !seen.has(String(m._id)))]);
      } catch {
        if (alive) setMaterials([]);
      }
    })();
    return () => { alive = false; };
  }, [id, batchId]);

  // ── Right-rail overview ──
  useEffect(() => {
    if (!batchId) return;
    let alive = true;
    apiClient
      .get(`/student/me/learning-overview?batchId=${batchId}`)
      .then((res) => { if (alive) setOverview(res.data || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [batchId]);

  // ── Resolve the smart-learning session this recording belongs to (if any) ──
  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    fetchSessionByVideo(id).then((res) => {
      if (!alive) return;
      setSmartSession(res?.session || null);
      setSmartProgress(res?.progress || null);
    });
    return () => { alive = false; };
  }, [id]);

  // ── Recent achievements (smart-learning courses only) ──
  useEffect(() => {
    const courseId = smartSession?.course;
    if (!courseId) { setAchievements([]); return undefined; }
    let alive = true;
    fetchGamification(courseId)
      .then((g) => { if (alive) setAchievements(g?.summary?.badges || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [smartSession?.course]);

  // ── Personal notes pad (per video, persisted locally) ──
  const [notes, setNotes] = useState("");
  const notesKey = `vc-session-notes-${id}`;
  useEffect(() => {
    try { setNotes(localStorage.getItem(notesKey) || ""); } catch { setNotes(""); }
  }, [notesKey]);
  const handleNotesChange = useCallback((e) => {
    const v = e.target.value;
    setNotes(v);
    try { localStorage.setItem(notesKey, v); } catch { /* storage unavailable */ }
  }, [notesKey]);

  // Keep local view-status fresh as watch progress is reported.
  const handleWatchProgress = useCallback((watchDuration, totalSec, lastPosition) => {
    if (!id) return;
    const percent = totalSec > 0 ? Math.min(100, Math.round((watchDuration / totalSec) * 100)) : (watchDuration > 0 ? 100 : 0);
    setViewStatus((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), viewed: true, watchDuration, lastPosition, percent },
    }));
  }, [id]);

  // ── Derived ──
  const title = rec?.topic || rec?.title || "Recording";
  const { status } = deriveStatus(viewStatus[id]);
  const statusMeta = STATUS_META[status];
  // Two different numbers, deliberately: the tracker accumulates on top of
  // `baselineSec` (total watched), while the player seeks to `resumeSec` (the
  // playhead). They coincide only for a student who never rewound.
  const baselineSec = viewStatus[id]?.watchDuration || 0;
  const resumeSec = viewStatus[id]?.lastPosition ?? baselineSec;

  // ── Smart-learning derived ──
  const isSession = !!smartSession;
  const displayTitle = smartSession?.title || title;
  const sessionUnlocked = !!smartSession?.resourcesUnlocked;
  const unlockCards = useMemo(
    () => (smartSession?.resources || []).filter((r) => r.kind !== "document"),
    [smartSession]
  );
  const sessionDocs = useMemo(
    () => (smartSession?.resources || []).filter((r) => r.kind === "document"),
    [smartSession]
  );
  const isResourceDone = useCallback((kind) => (
    (kind === "assignment" && smartProgress?.assignmentCompleted) ||
    (kind === "codingChallenge" && smartProgress?.codingCompleted) ||
    (kind === "quiz" && smartProgress?.quizCompleted)
  ), [smartProgress]);
  const sessionProgressPct = smartProgress?.progressPercent || 0;

  // Session videos get Notes / Ask AI Doubt / Discussion in addition to
  // Overview + Resources; plain videos keep the original two-tab layout.
  const tabList = useMemo(() => (
    isSession
      ? [
          { key: "overview", label: "Overview" },
          { key: "notes", label: "Notes" },
          { key: "resources", label: "Resources" },
          { key: "ai", label: "Ask AI Doubt" },
          { key: "discussion", label: "Discussion" },
        ]
      : [
          { key: "overview", label: "Overview" },
          { key: "resources", label: "Resources" },
        ]
  ), [isSession]);

  // Keep the active tab valid as the tab set changes.
  useEffect(() => {
    if (!tabList.some((t) => t.key === tab)) setTab("overview");
  }, [tabList, tab]);

  const askAiDoubt = useCallback(() => {
    const crumbs = [smartSession?.moduleTitle, smartSession?.title].filter(Boolean);
    const prefill = crumbs.length ? `I have a doubt about "${crumbs.join(" → ")}". ` : "";
    if (hasPlanFeature("aiDoubtSolving")) {
      window.dispatchEvent(new CustomEvent("vc-open-aivo", {
        detail: {
          prefill,
          context: {
            courseId: smartSession?.course,
            moduleNumber: smartSession?.moduleNumber,
            sessionId: smartSession?._id,
          },
        },
      }));
    } else {
      navigate("/s/learn-with-ai");
    }
  }, [smartSession, hasPlanFeature, navigate]);

  // Prev/Next in chronological (course) order
  const ordered = useMemo(
    () => [...recordings].sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()),
    [recordings]
  );
  const curIdx = ordered.findIndex((v) => v._id === id);
  const prevRec = curIdx > 0 ? ordered[curIdx - 1] : null;
  const nextRec = curIdx >= 0 && curIdx < ordered.length - 1 ? ordered[curIdx + 1] : null;

  const cont = overview?.continueLearning;
  const progress = overview?.progress;
  const progressSegments = progress
    ? Object.keys(PROGRESS_LABELS).map((key) => ({
        key,
        label: PROGRESS_LABELS[key],
        color: PROGRESS_COLORS[key],
        done: progress[key]?.done || 0,
        total: progress[key]?.total || 0,
      }))
    : [];

  if (loading) {
    return (
      <div className="student-video-player">
        <div className="svp-page-loading"><Spinner animation="border" /></div>
      </div>
    );
  }

  if (!rec) {
    return (
      <div className="student-video-player">
        <div className="svp-page-loading"><p>This video isn’t available or you don’t have access to it.</p></div>
      </div>
    );
  }

  return (
    <div className="student-video-player">
      {/* Breadcrumb */}
      <nav className="svp-crumbs" aria-label="Breadcrumb">
        <button type="button" onClick={() => navigate("/s/learning")}>
          {isSession ? "Classes" : "Courses"}
        </button>
        <ChevronRight size={14} />
        {isSession ? (
          smartSession.moduleNumber != null && (
            <>
              <button type="button" onClick={() => navigate("/s/learning")}>
                Module {smartSession.moduleNumber}
                {smartSession.moduleTitle ? `: ${smartSession.moduleTitle}` : ""}
              </button>
              <ChevronRight size={14} />
            </>
          )
        ) : (
          course?.name && (
            <>
              <button type="button" onClick={() => navigate("/s/learning")}>{course.name}</button>
              <ChevronRight size={14} />
            </>
          )
        )}
        <span className="is-current">{displayTitle}</span>
      </nav>

      <div className="svp-body">
        {/* ── Main column ── */}
        <div className="svp-main">
          {/* Player */}
          <div className="svp-player">
            {requireDesktopApp ? (
              <DesktopAppRequired />
            ) : guard === "blocked" ? (
              <PlaybackBlocked reasons={guardReasons} checking={false} onRecheck={runGuard} />
            ) : guard === "checking" ? (
              <div className="svp-player__stage">
                <div className="svp-player__center">
                  <Spinner animation="border" variant="light" />
                  <p>Preparing secure playback…</p>
                </div>
              </div>
            ) : (
              <SecureVideo
                url={videoUrl}
                poster={courseThumb || rec.thumbnailUrl}
                loading={streamLoading}
                title={displayTitle}
                videoId={id}
                durationMin={rec.duration || 0}
                baselineSec={baselineSec}
                resumeSec={resumeSec}
                onProgress={handleWatchProgress}
                onExpired={refreshStreamUrl}
              />
            )}
          </div>

          {/* Info card (no video download) */}
          <div className="svp-info">
            <div className="svp-info__top">
              <div className="svp-info__title-wrap">
                <h1 className="svp-info__title">{displayTitle}</h1>
                {statusMeta && (
                  <span className={`svp-status ${statusMeta.cls}`}>
                    <statusMeta.Icon size={13} /> {statusMeta.label}
                  </span>
                )}
              </div>
              <div className="svp-info__actions">
                {isSession && sessionDocs.length > 0 && (
                  <button
                    type="button"
                    className="svp-btn svp-btn--outline"
                    onClick={() => setTab("resources")}
                    title="Session materials"
                  >
                    <Download size={15} /> Download
                  </button>
                )}
                <button
                  type="button"
                  className={`svp-iconbtn${saved ? " is-active" : ""}`}
                  aria-label={saved ? "Remove from saved videos" : "Save for later"}
                  title={saved ? "Saved — tap to remove" : "Save for later"}
                  aria-pressed={saved}
                  onClick={toggleSaved}
                >
                  <Bookmark size={16} fill={saved ? "currentColor" : "none"} />
                </button>
              </div>
            </div>

            <div className="svp-info__meta">
              {rec.recordedAt && <span className="svp-info__meta-item"><Calendar size={13} /> {formatDate(rec.recordedAt)}</span>}
              {rec.duration > 0 && <span className="svp-info__meta-item"><Clock size={13} /> {formatDuration(rec.duration)}</span>}
              {rec.uploadedBy?.name && <span className="svp-info__meta-item"><User size={13} /> By {rec.uploadedBy.name}</span>}
              {isSession && course?.name && <span className="svp-chip">{course.name}</span>}
              {isSession && smartSession.moduleNumber != null && (
                <span className="svp-chip">Module {smartSession.moduleNumber}</span>
              )}
              {!isSession && rec.fileType && <span className="svp-filetype">{String(rec.fileType).toUpperCase()}</span>}
            </div>
          </div>

          {/* Tabs card */}
          <div className="svp-tabs-card">
            <div className="svp-tabs" role="tablist">
              {tabList.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  className={`svp-tab${tab === t.key ? " is-active" : ""}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="svp-tab-panel">
              {tab === "overview" &&
                (isSession ? (
                  <>
                    <div className="svp-overview">
                      <div className="svp-overview__about">
                        <h3 className="svp-panel__title">About this session</h3>
                        <p className="svp-panel__text">
                          {smartSession.description || rec.description || "No summary provided for this session."}
                        </p>
                      </div>
                      <div className="svp-overview__progress">
                        <span className="svp-overview__progress-label">Your Progress</span>
                        <MiniRing
                          value={sessionProgressPct}
                          caption={sessionProgressPct >= 80 ? "Great job! Keep it up." : "Keep going!"}
                        />
                      </div>
                    </div>

                    {unlockCards.length > 0 && (
                      <div className="svp-unlocked">
                        <h3 className="svp-panel__title">What's Unlocked</h3>
                        <div className="svp-unlocked__grid">
                          {unlockCards.map((res, i) => (
                            <UnlockedCard
                              key={`${res.refId}-${i}`}
                              res={res}
                              unlocked={sessionUnlocked}
                              done={isResourceDone(res.kind)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <h3 className="svp-panel__title">About this video</h3>
                    <p className="svp-panel__text">{rec.description || "No description provided for this video."}</p>
                  </>
                ))}

              {tab === "notes" && (
                <div className="svp-notes">
                  <h3 className="svp-panel__title">My Notes</h3>
                  <p className="svp-panel__hint">Jot down key points while you watch — saved on this device.</p>
                  <textarea
                    className="svp-notes__area"
                    placeholder="Type your notes here…"
                    value={notes}
                    onChange={handleNotesChange}
                    rows={8}
                  />
                </div>
              )}

              {tab === "resources" &&
                (isSession ? (
                  sessionDocs.length > 0 ? (
                    <ul className="svp-reslist">
                      {sessionDocs.map((doc, i) => (
                        <SessionDocRow key={`${doc.refId}-${i}`} doc={doc} unlocked={sessionUnlocked} onOpen={openMaterial} />
                      ))}
                    </ul>
                  ) : (
                    <p className="svp-panel__text">No notes or reference material linked to this session yet.</p>
                  )
                ) : materials.length > 0 ? (
                  <ul className="svp-reslist">
                    {materials.map((mat) => (
                      <MaterialRow key={mat._id} mat={mat} onView={openMaterial} onDownload={downloadMaterial} />
                    ))}
                  </ul>
                ) : (
                  <p className="svp-panel__text">No resources attached to this video.</p>
                ))}

              {tab === "ai" && (
                <div className="svp-aipanel">
                  <span className="svp-aipanel__icon"><Sparkles size={22} /></span>
                  <h3 className="svp-aipanel__title">AI Study Assistant</h3>
                  <p className="svp-aipanel__text">
                    Ask doubts about this session, get concepts explained, or generate revision
                    notes — answers use this session's context.
                  </p>
                  <button type="button" className="svp-aipanel__cta" onClick={askAiDoubt}>
                    <MessageCircle size={15} /> Ask AI Doubt
                  </button>
                </div>
              )}

              {tab === "discussion" && smartSession && (
                <SessionDiscussionTab sessionId={smartSession._id} />
              )}
            </div>

            <div className="svp-tabs-card__footer">
              <button type="button" className="svp-btn svp-btn--outline" disabled={!prevRec}
                onClick={() => prevRec && navigate(`/s/watch-v2/${prevRec._id}`)}>
                <ArrowLeft size={16} /> {isSession ? "Previous Session" : "Previous Video"}
              </button>
              <button type="button" className="svp-btn svp-btn--primary" disabled={!nextRec}
                onClick={() => nextRec && navigate(`/s/watch-v2/${nextRec._id}`)}>
                {isSession ? "Next Session" : "Next Video"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Right rail ── */}
        <aside className="svp-rail">
          {/* Materials */}
          <div className="svp-widget">
            <div className="svp-widget__head">
              <h3 className="svp-widget__title">
                {isSession ? "Materials for this session" : "Materials for this video"}
              </h3>
            </div>
            {isSession ? (
              sessionDocs.length > 0 ? (
                <ul className="svp-reslist">
                  {sessionDocs.map((doc, i) => (
                    <SessionDocRow key={`${doc.refId}-${i}`} doc={doc} unlocked={sessionUnlocked} onOpen={openMaterial} />
                  ))}
                </ul>
              ) : (
                <p className="svp-help__text">No materials for this session yet.</p>
              )
            ) : materials.length > 0 ? (
              <ul className="svp-reslist">
                {materials.map((mat) => (
                  <MaterialRow key={mat._id} mat={mat} onView={openMaterial} onDownload={downloadMaterial} />
                ))}
              </ul>
            ) : (
              <p className="svp-help__text">No materials for this video.</p>
            )}
          </div>

          {/* Your Progress */}
          {progress && (
            <div className="svp-widget">
              <div className="svp-widget__head">
                <h3 className="svp-widget__title">Your Progress</h3>
                <button type="button" className="svp-widget__link" onClick={() => navigate("/s/dashboard")}>View Details</button>
              </div>
              <div className="svp-progress">
                <ProgressDonut overall={progress.overall || 0} segments={progressSegments} />
                <ul className="svp-progress__legend">
                  {progressSegments.map((l) => (
                    <li key={l.key} className="svp-progress__item">
                      <span className="svp-progress__dot" style={{ background: l.color }} />
                      <span className="svp-progress__name">{l.label}</span>
                      <span className="svp-progress__count">{l.done} / {l.total}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Continue Learning */}
          {cont && (
            <div className="svp-widget">
              <div className="svp-widget__head">
                <h3 className="svp-widget__title">Continue Learning</h3>
                <button type="button" className="svp-widget__link" onClick={() => navigate("/s/learning")}>View All</button>
              </div>
              <div className="svp-continue">
                <div className="svp-continue__row">
                  <div className="svp-continue__thumb">
                    {cont.thumbnailUrl ? <img src={cont.thumbnailUrl} alt={cont.title} loading="lazy" /> : <Film size={18} />}
                  </div>
                  <div className="svp-continue__info">
                    <h4 className="svp-continue__title">{cont.title}</h4>
                    {cont.subtitle && <p className="svp-continue__sub">{cont.subtitle}</p>}
                  </div>
                </div>
                <div className="svp-continue__progress">
                  <div className="svp-continue__bar">
                    <div className="svp-continue__bar-fill" style={{ width: `${cont.percent || 0}%` }} />
                  </div>
                  <span className="svp-continue__pct">{cont.percent || 0}%</span>
                </div>
                <button type="button" className="svp-continue__cta"
                  onClick={() => cont.videoId && navigate(`/s/watch-v2/${cont.videoId}`)}>
                  <Play size={15} /> Continue Watching
                </button>
              </div>
            </div>
          )}

          {/* Have a doubt? / Need Help */}
          <div className="svp-widget">
            <div className="svp-widget__head">
              <h3 className="svp-widget__title">{isSession ? "Have a doubt?" : "Need Help?"}</h3>
            </div>
            <p className="svp-help__text">Ask our AI assistant and get instant help.</p>
            <button
              type="button"
              className="svp-help__cta"
              onClick={isSession ? askAiDoubt : () => navigate("/s/learn-with-ai")}
            >
              <Sparkles size={15} /> Ask AI Doubt
            </button>
          </div>

          {/* Recent Achievements (smart-learning only) */}
          {isSession && achievements.length > 0 && (
            <div className="svp-widget">
              <div className="svp-widget__head">
                <h3 className="svp-widget__title">Recent Achievements</h3>
              </div>
              <ul className="svp-ach">
                {achievements.slice(0, 3).map((b) => (
                  <li key={b.code} className="svp-ach__item">
                    <span className="svp-ach__icon"><Award size={16} /></span>
                    <div className="svp-ach__body">
                      <span className="svp-ach__title">{b.label || b.code}</span>
                      {b.description && <span className="svp-ach__sub">{b.description}</span>}
                    </div>
                    {b.xp ? <span className="svp-ach__xp">+{b.xp} XP</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {/* In-app document viewer for video-attached materials */}
      {materialViewer}
    </div>
  );
};

export default StudentVideoPlayer;
